import type { TFunction } from "i18next";

import { formatDate, formatShortDayMonth, formatTime } from "@ergon-ai/utils";

/**
 * Formats a date range into a localized, human-readable string.
 *
 * - Displays "countdown" if the event is **within the next hour**.
 * - Displays "Today" if the event is **on the current day**.
 * - Displays "Tomorrow" if the event is **the next day**.
 * - Otherwise, formats the date with `formatDate()`.
 *
 * @param dateString - The start date as an **ISO string** (e.g., `"2025-02-01T14:00:00Z"`).
 * @param endDateString - The end date as an **ISO string** (e.g., `"2025-02-01T16:00:00Z"`).
 * @param t - The **i18next** translation function.
 * @param locale - The locale string (e.g., `"en-GB"`, `"it-IT"`). Defaults to `"en-GB"`.
 * @param options - Optional configuration object.
 * @param options.useAbsoluteDates - If true, always uses absolute dates instead of "Today"/"Tomorrow". Useful for consistent test snapshots.
 * @param options.timeZone - Optional IANA zone the whole range resolves in.
 *   Omitted means the device's own, which is right for a screen and wrong for
 *   a stored record two people in different countries must both read.
 * @param options.style - `"short"` is the Activities chip (Figma 7485:6608): "18 Mar · 10:00-11:00",
 *   "Tomorrow · 10:00-12:00". The default keeps "DD/MM/YYYY - HH:MM/HH:MM".
 * @returns A localized string representing the date range with time context.
 *
 * @example
 * ```ts
 * getFormattedDateRange("2025-02-01T14:00:00Z", "2025-02-01T16:00:00Z", t, "en-GB");
 * // Returns: "Today - 14:00/16:00"
 *
 * getFormattedDateRange("2025-02-02T14:00:00Z", "2025-02-02T16:00:00Z", t, "en-GB");
 * // Returns: "Tomorrow - 14:00/16:00"
 *
 * getFormattedDateRange("2025-02-05T14:00:00Z", "2025-02-05T16:00:00Z", t, "en-GB");
 * // Returns: "05/02/2025 - 14:00/16:00" (Formatted date)
 *
 * // With absolute dates for consistent test snapshots:
 * getFormattedDateRange("2025-02-01T14:00:00Z", "2025-02-01T16:00:00Z", t, "en-GB", { useAbsoluteDates: true });
 * // Returns: "01/02/2025 - 14:00/16:00"
 * ```
 */
export const getFormattedDateRange = (
  dateString: string,
  endDateString: string,
  t: TFunction<"translation", undefined>,
  locale: string,
  options?: {
    useAbsoluteDates?: boolean;
    timeZone?: string;
    style?: "default" | "short";
  },
): string => {
  const now = new Date();
  const timeZone = options?.timeZone;
  const date = new Date(dateString);
  const endDate = new Date(endDateString);

  // Normalize to UTC to ensure date comparisons are correct
  const nowUTC = new Date(
    Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()),
  );
  const dateUTC = new Date(
    Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()),
  );

  const diffInMinutes = Math.round((date.getTime() - now.getTime()) / 60000); // Minutes until the event
  const diffInDays = Math.floor(
    (dateUTC.getTime() - nowUTC.getTime()) / (1000 * 60 * 60 * 24),
  ); // Days until the event

  const endTime = formatTime(endDate, locale, timeZone);
  const startTime = formatTime(date, locale, timeZone);
  const short = options?.style === "short";
  const join = (when: string) =>
    short
      ? `${when} · ${startTime}-${endTime}`
      : `${when} - ${startTime}/${endTime}`;
  const day = () =>
    short
      ? formatShortDayMonth(date, locale, timeZone)
      : formatDate(date, locale, timeZone);

  // 🔹 If useAbsoluteDates is true, always return formatted date (useful for test consistency)
  if (options?.useAbsoluteDates) {
    return join(day());
  }

  // 🔹 If event is within the next hour → "countdown"
  if (diffInMinutes <= 60 && diffInMinutes > 0) {
    return join(t("date.countdown", { count: diffInMinutes }));
  }

  // 🔹 If event is today → "Today - HH:MM/HH:MM"
  if (diffInDays === 0) {
    return join(t("date.today"));
  }

  // 🔹 If event is tomorrow → "Tomorrow - HH:MM/HH:MM"
  if (diffInDays === 1) {
    return join(t("date.tomorrow"));
  }

  // 🔹 Otherwise, return full formatted date → "DD/MM/YYYY - HH:MM/HH:MM"
  return join(day());
};
